import { RefObject, useCallback, useMemo } from 'react';

import { Theme } from '@mui/material';
import { ChartEventsOptions, dateFormat, PlotOptions, XAxisOptions } from 'highcharts';
import Highcharts from 'highcharts/highstock';

import { WatermarkAttributes } from '@/components/Chart/types/highcharts';
import { useAppSelector } from '@/hooks/redux-hooks';
import { detectEnvironment } from '@/utils/detectEnvironment';
import sliceConfigFormat from '@/utils/sliceConfigFormat';

export const CHART_GRID_COLOR = '#333';

export interface ChartDownloadProperties {
  format: string;
  fullScreen?: boolean;
  darkMode?: boolean;
  chartLocation: ChartParentComponent | null;
}

export interface ChartParentComponent {
  mainPageTitle: string;
  subPageTitle?: string;
  tabName?: string;
}

export interface ExtendedPlotOptions extends PlotOptions {
  custom?: object;
}
const createYAxis = (color: string) => ({
  title: { text: undefined },
  maxPadding: 0.1,
  offset: 35,
  opposite: true,
  labels: {
    style: {
      color,
      fontSize: '10px'
    },
    formatter(): string {
      // @ts-expect-error: highcharts rules.
      return this.value.toFixed(2);
    }
  },
  gridLineColor: CHART_GRID_COLOR
});

export const getBaseImageUrl = () => {
  const environment = detectEnvironment();
  return `https://${environment === 'staging' ? 'staging.' : ''}go.g-x.${environment === 'dev' ? 'dev' : 'co'}`;
};
export const useChartFunctions = () => {
  const { userConfig } = useAppSelector(state => state.userConfig);

  const userDateFormat = useMemo(() => {
    let configFormat = userConfig?.dateFormat.value;
    const firstOccurrence = configFormat.indexOf('d');
    const lastOccurrence = configFormat.lastIndexOf('d');

    configFormat = sliceConfigFormat(firstOccurrence, configFormat, lastOccurrence);
    const delimiter = configFormat.includes('-') ? '-' : '/';
    const configParts = configFormat.split(delimiter);

    configFormat = configParts
      .map(config => {
        switch (config.toLowerCase()) {
          case 'mmm': {
            return '%b';
          }
          case 'mm': {
            return '%m';
          }
          case 'yy': {
            return '%y';
          }
          case 'yyyy': {
            return '%Y';
          }
          default:
            return null;
        }
      })
      .filter(Boolean)
      .join(delimiter);
    return configFormat;
  }, [userConfig]);
  const createEventOptions = (
    watermarkImage: RefObject<Highcharts.SVGElement | null>,
    getWatermarkAttributes: (chart: Highcharts.Chart) => WatermarkAttributes,
    watermarkImageName: string
  ): ChartEventsOptions => {
    return {
      load() {
        const attrs = getWatermarkAttributes(this as unknown as Highcharts.Chart);
        watermarkImage.current = this.renderer
          .image(getBaseImageUrl() + watermarkImageName)
          .attr(attrs)
          .add();
      },
      redraw() {
        const imageUrl = getBaseImageUrl() + watermarkImageName;

        if (watermarkImage.current) {
          const attrs = getWatermarkAttributes(this as unknown as Highcharts.Chart);
          watermarkImage.current.attr({
            href: imageUrl,
            ...attrs
          });
        }
      }
    };
  };

  const createNavigator = useCallback(
    (theme: Theme) => {
      return {
        xAxis: {
          gridLineColor: theme.palette.info.dark,
          labels: {
            style: {
              color: theme.palette.text.primary
            },
            formatter(): string {
              // @ts-expect-error: highcharts rules.
              const value = this.value;
              return dateFormat(userDateFormat, value);
            }
          }
        },
        enabled: true,
        adaptToUpdatedData: false,
        scrollbar: { enabled: true, barBackgroundColor: theme.palette.info.dark },
        outlineColor: theme.palette.info.dark,
        handles: {
          backgroundColor: theme.palette.background.default,
          borderColor: theme.palette.info.dark
        }
      };
    },
    [userDateFormat]
  );

  const createPlotOptions = useCallback(() => {
    const plotOptions: ExtendedPlotOptions = {};
    plotOptions.series = {
      animation: false,
      states: {
        inactive: {
          opacity: 1
        },
        hover: {
          enabled: false
        }
      }
    };
    plotOptions.line = {
      states: {
        hover: {
          lineWidth: 3
        }
      }
    };
    return plotOptions;
  }, []);

  const createXAxis = useCallback(
    (color: string): XAxisOptions => {
      return {
        tickInterval: 30 * 24 * 3600 * 1000,
        ordinal: false,
        labels: {
          formatter(): string {
            const value = Number(this.value);
            return dateFormat(userDateFormat, value);
          },
          style: {
            color,
            fontSize: '10px'
          }
        },
        gridLineColor: CHART_GRID_COLOR,
        lineColor: CHART_GRID_COLOR,
        tickColor: CHART_GRID_COLOR
      };
    },
    [userDateFormat]
  );

  return {
    createNavigator,
    createPlotOptions,
    createYAxis,
    createXAxis,
    createEventOptions
  };
};

export default useChartFunctions;
